import Section from '../components/Section';

/**
 * Static changelog. Deliberately NO API / fetch / backend call — the entire
 * history is compiled into the bundle at build time. That means this page has
 * zero request surface: nothing here can be probed, injected into, or used to
 * enumerate the backend. To publish a new entry, edit RELEASES below and
 * redeploy the frontend. Keep it factual and user-facing; never list internal
 * endpoint names, table names, secrets, or infra details here.
 */

type Tag = 'new' | 'fix' | 'security';
interface Change { tag: Tag; text: string; }
interface Release { version: string; date: string; platform: string; changes: Change[]; }

const RELEASES: Release[] = [
  {
    version: '1.0.6',
    date: '2026-08-28',
    platform: 'Android · Windows 1.0.2',
    changes: [
      { tag: 'new', text: 'Email verification at sign-up — new accounts confirm a 6-digit code sent to your inbox.' },
      { tag: 'new', text: 'Your login method (Email or Google) now shows in your account.' },
      { tag: 'new', text: 'Improved refund handling for cancelled plans.' },
      { tag: 'fix', text: 'Support replies and notifications now arrive reliably.' },
      { tag: 'fix', text: 'More dependable connect and reconnect.' },
    ],
  },
  {
    version: '1.0.4',
    date: '2026-08-27',
    platform: 'Android',
    changes: [
      { tag: 'fix', text: 'Fixed a case where the tunnel repeatedly reconnected instead of staying connected.' },
      { tag: 'new', text: 'Repay a pending invoice directly from the Invoices screen.' },
      { tag: 'new', text: 'Ready for the latest Android release.' },
    ],
  },
  {
    version: '1.0.1',
    date: '2026-08-26',
    platform: 'Android',
    changes: [
      { tag: 'new', text: 'Delete your account in-app (Account → Danger zone).' },
      { tag: 'new', text: 'Terms & Privacy consent shown on first launch.' },
      { tag: 'new', text: 'Quick Settings tile to connect or disconnect from the notification shade.' },
      { tag: 'security', text: 'Post-quantum encrypted, zero-log tunnel.' },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-08-15',
    platform: 'Android · Windows',
    changes: [
      { tag: 'new', text: 'First public release: one-tap VPN, free tier with no card, multi-device sign-in.' },
    ],
  },
];

const TAG_STYLE: Record<Tag, { label: string; cls: string }> = {
  new:      { label: 'New', cls: 'bg-[color:var(--color-ok)]/15 text-[color:var(--color-ok)] border-[color:var(--color-ok)]/30' },
  fix:      { label: 'Fix', cls: 'bg-[color:var(--color-brand)]/15 text-[color:var(--color-brand-glow)] border-[color:var(--color-brand)]/30' },
  security: { label: 'Security', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
};

export default function Changelog() {
  return (
    <Section chip="Product" title="Changelog" subtitle="Every Vpnee release, newest first. This page is fully static — no tracking, no accounts, nothing to log in for.">
      <div className="max-w-3xl">
        <ol className="relative border-l border-[color:var(--color-line-2)] pl-6 space-y-8">
          {RELEASES.map((r) => (
            <li key={r.version} className="relative">
              <span className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full ring-4 ring-[color:var(--color-bg)] bg-gradient-to-br from-[color:var(--color-brand)] to-[color:var(--color-brand-deep)]" />
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="h-title text-xl font-extrabold">v{r.version}</span>
                <span className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">{r.date}</span>
                <span className="chip !py-0 !text-[10px]">{r.platform}</span>
              </div>
              <ul className="mt-3 space-y-2">
                {r.changes.map((c, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[color:var(--color-ink-dim)] leading-relaxed">
                    <span className={`shrink-0 mt-0.5 inline-flex px-1.5 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wide ${TAG_STYLE[c.tag].cls}`}>{TAG_STYLE[c.tag].label}</span>
                    <span>{c.text}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
        <p className="mt-10 text-xs text-[color:var(--color-ink-faint)]">
          Looking for older builds or the full technical history? Contact <a href="mailto:hello@tnidu.com" className="text-[color:var(--color-brand-glow)] hover:underline">hello@tnidu.com</a>.
        </p>
      </div>
    </Section>
  );
}
